// Signed-in shell — mirrors the local frontend's App.tsx layout: a sticky
// backdrop-blur topbar spanning both grid columns, and a collapsible sticky
// sidebar (15rem ↔ 4rem, floating round toggle on its edge) with nav-item
// entries. Inside a site's Manage area the sidebar (and the topbar's phone
// dropdown) additionally shows the SAME two-level device menu as the local
// FE — see device/DeviceNavMenu.tsx.
import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { CreditCard, LayoutGrid, Network, Shield, UserRound } from "../../components/icons";
import { useAuth } from "../../auth/AuthContext";
import { AppTopbar, type AppNavItem } from "../../components/AppTopbar";
import { DeviceNavMenu, deviceSiteIdFromPath } from "../../device/DeviceNavMenu";
import { UnverifiedBanner } from "./UnverifiedBanner";

const COLLAPSE_KEY = "icc.sidebarCollapsed";

export function AppLayout() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === "1";
    } catch {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(COLLAPSE_KEY, next ? "1" : "0");
      } catch {
        /* ignore */
      }
      return next;
    });
  };

  // Fleet appears only with a Fleet subscription — or for members of someone
  // else's fleet organization (their access rides on the owner's plan).
  const showFleet =
    !!user && (user.features.includes("fleet_dashboard") || user.org != null);

  const nav: AppNavItem[] = [
    { to: "/app", end: true, icon: LayoutGrid, label: t("nav.sites") },
    ...(showFleet ? [{ to: "/app/fleet", icon: Network, label: t("nav.fleet") }] : []),
    { to: "/app/subscription", icon: CreditCard, label: t("nav.subscription") },
    { to: "/app/account", icon: UserRound, label: t("nav.account") },
    ...(user?.is_admin ? [{ to: "/app/admin", icon: Shield, label: t("nav.admin") }] : []),
  ];

  // Inside /app/sites/{id}/device/* the sidebar grows the device menu.
  const deviceSiteId = deviceSiteIdFromPath(location.pathname);

  return (
    <div
      className={`min-h-full grid grid-rows-[auto_1fr] ${
        collapsed ? "md:grid-cols-[4rem_1fr]" : "md:grid-cols-[15rem_1fr]"
      }`}
    >
      <header className="md:col-span-2 border-b border-border bg-panel/60 backdrop-blur sticky top-0 z-20 relative">
        <AppTopbar nav={nav} />
      </header>

      <aside className="hidden md:flex md:flex-col border-r border-border bg-panel/40 p-3 gap-2 sticky top-14 self-start h-[calc(100vh-3.5rem)] overflow-visible relative">
        <button
          type="button"
          onClick={toggleCollapsed}
          className="absolute top-1/2 -right-3 -translate-y-1/2 z-10 inline-flex items-center justify-center h-6 w-6 rounded-full border border-border bg-panel text-muted hover:text-text hover:bg-panel2 shadow-sm transition"
          title={collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")}
          aria-label={collapsed ? t("nav.expandSidebar") : t("nav.collapseSidebar")}
        >
          {collapsed ? <PanelLeftOpen size={14} /> : <PanelLeftClose size={14} />}
        </button>

        <div className="flex flex-col gap-1">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `nav-item relative ${isActive ? "active" : ""} ${collapsed ? "justify-center" : ""}`
              }
              title={collapsed ? item.label : undefined}
            >
              <item.icon size={18} className="shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </div>

        {deviceSiteId != null && (
          <DeviceNavMenu
            siteId={deviceSiteId}
            pathname={location.pathname}
            collapsed={collapsed}
          />
        )}
      </aside>

      <main className="p-4 md:p-6 overflow-x-hidden">
        <UnverifiedBanner />
        <Outlet />
      </main>
    </div>
  );
}
